import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiRequest, getQueryFn } from '@/lib/queryClient';
import { persistentAuth } from '@/services/persistentAuth';
import { profilePersistence } from '@/services/profilePersistence';
import type { LoginData } from '@shared/client-types';

export function useAuth() {
  const [persistentAuthUser, setPersistentAuthUser] = useState<any>(null);
  const [persistentAuthReady, setPersistentAuthReady] = useState(false);

  // Wait for persistent auth to finish initialize() before /api/auth/user
  useEffect(() => {
    let mounted = true;

    persistentAuth
      .whenReady()
      .catch(() => false)
      .finally(() => {
        if (mounted) {
          setPersistentAuthReady(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['/api/auth/user'],
    queryFn: getQueryFn({ on401: 'returnNull' }),
    enabled: persistentAuthReady, // CRITICAL: Don't run until persistent auth is ready
    retry: 2, // Retry transient errors before failing auth (prevents random logouts)
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  const queryClient = useQueryClient();

  // Listen for persistent auth state changes (automatic session restoration only)
  useEffect(() => {
    if (!queryClient) return;

    const handleAuthStateChange = (event: CustomEvent) => {
      const { user: authUser, authenticated } = event.detail;

      if (authenticated && authUser) {
        setPersistentAuthUser(authUser);
        // Merge into existing cache so we don't wipe richer /api/auth/user fields
        // (e.g. profileImageUrl) with a thin refresh/session snapshot.
        queryClient.setQueryData(['/api/auth/user'], (old: any) =>
          old ? { ...old, ...authUser } : authUser
        );
      } else {
        setPersistentAuthUser(null);
        queryClient.setQueryData(['/api/auth/user'], null);
      }
    };

    window.addEventListener('auth-state-change', handleAuthStateChange as EventListener);

    return () => {
      window.removeEventListener('auth-state-change', handleAuthStateChange as EventListener);
    };
  }, [queryClient]);

  const loginMutation = useMutation({
    mutationFn: async (data: LoginData) => {
      const response = await apiRequest('POST', '/api/auth/login', data);
      const result = await response.json();

      // Handle email verification error
      if (!response.ok && result.code === 'EMAIL_NOT_VERIFIED') {
        throw new Error(`EMAIL_NOT_VERIFIED:${result.email}`);
      }

      return result;
    },
    onSuccess: () => {
      // CRITICAL FIX: Clear all stale cache data on explicit login
      // This ensures fresh volume data is loaded after logout → login cycle
      // NOTE: This only runs on explicit login via login form, NOT on automatic session restoration
      console.log('🔄 Explicit login - clearing stale cache for fresh data');

      // Clear all cached data to force fresh fetch after login
      queryClient.clear();

      // Then invalidate auth query to trigger re-fetch
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Use persistent auth service for logout
      await persistentAuth.logout();
    },
    onSuccess: () => {
      setPersistentAuthUser(null);
      // Clear profile persistence data on logout
      profilePersistence.clearProfileData();
      if (queryClient) {
        queryClient.setQueryData(['/api/auth/user'], null);
        queryClient.clear();
      }
    },
  });

  const refreshSessionMutation = useMutation({
    mutationFn: async () => {
      const success = await persistentAuth.refreshSession({
        clearOnFailure: false,
        force: true,
      });
      if (!success) {
        throw new Error('Session refresh failed');
      }
      // Return current user from query cache / will be set via auth-state-change
      return { user: queryClient.getQueryData(['/api/auth/user']) };
    },
    onSuccess: (data) => {
      if (data?.user && queryClient) {
        queryClient.setQueryData(['/api/auth/user'], data.user);
        setPersistentAuthUser(data.user);
      }
    },
    onError: (error) => {
      // Do not clear auth here — persistentAuth already decided whether session is alive
      console.error('Session refresh failed:', error);
    },
  });

  // Prefer the network user when present so profile fields (e.g. profileImageUrl)
  // from /api/auth/user are not overwritten by a thin refresh/session snapshot.
  const currentUser = user
    ? persistentAuthUser
      ? { ...persistentAuthUser, ...user }
      : user
    : persistentAuthUser;

  return {
    user: currentUser,
    isLoading: !persistentAuthReady || isLoading, // Loading until persistent auth is ready
    isAuthenticated: !!currentUser,
    login: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    logout: logoutMutation.mutateAsync,
    isLoggingOut: logoutMutation.isPending,
    refreshSession: refreshSessionMutation.mutateAsync,
    isRefreshing: refreshSessionMutation.isPending,
    isPersistentAuth: !!persistentAuthUser,
  };
}
